import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Checkbox,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Refresh
} from '@mui/icons-material';
import { format, subWeeks, subDays } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { OrderDetailsModal } from './UpcomingCollectionsModal';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom green icon for completed collections
const completedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMTBiOTgxIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

interface CollectionEvent {
  id: number;
  order_id: number;
  woo_order_id: number;
  order_number: string;
  scheduled_date: string;
  status: 'pending' | 'assigned' | 'completed' | 'cancelled';
  assigned_driver_id?: number;
  driver_name?: string;
  current_driver_name?: string;
  driver_code?: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  loyalty_member?: number;
  previous_orders_count?: number;
  customer_phone?: string;
  delivery_address_1: string;
  delivery_postcode: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  service_type?: string;
  product_names?: string;
  bins_total_services?: number;
  bags_total_services?: number;
  large_bins_total_services?: number;
  beds_count?: number;
  mattress_count?: number;
  total?: string;
  notes?: string;
  completed_at?: string;
  completed_by?: number;
  created_at?: string;
  updated_at?: string;
  customer_note?: string;
  items?: string;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
}

interface Driver {
  id: number;
  name: string;
  driver_code: string;
  status: string;
}

interface Order {
  id: number;
  woo_order_id: number;
  order_number: string;
  status: string;
  collection_status: string;
  total: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email?: string;
  customer_phone?: string;
  delivery_postcode: string;
  shipping_collection_date: string;
  collection_dates?: string;
  service_type?: string;
  product_names?: string;
  driver_name?: string;
  assigned_user_id?: number;
  assigned_driver_id?: number;
  // Additional fields for detailed view
  delivery_address_1?: string;
  delivery_address_2?: string;
  delivery_city?: string;
  delivery_country?: string;
  billing_address_1?: string;
  billing_address_2?: string;
  billing_city?: string;
  billing_postcode?: string;
  billing_country?: string;
  date_created?: string;
  assigned_at?: string;
  completed_at?: string;
  customer_note?: string;
  bins_per_occasion?: number;
  bags_per_occasion?: number;
  bins_number_of_occasions?: number;
  bags_number_of_occasions?: number;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
  total_collections?: number;
  completed_collections?: number;
  pending_collections?: number;
}

const PreviousCollections: React.FC = () => {
  const [events, setEvents] = useState<CollectionEvent[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });
  const [orderCollectionEvents, setOrderCollectionEvents] = useState<CollectionEvent[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dateRange, setDateRange] = useState({ start: subWeeks(new Date(), 4), end: new Date() });
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [weeksBack, setWeeksBack] = useState(4); // Default to 4 weeks back

  // Map-specific state (independent from table)
  const [mapEvents, setMapEvents] = useState<CollectionEvent[]>([]);
  const [mapDate, setMapDate] = useState(() => {
    // Default to yesterday
    const yesterday = subDays(new Date(), 1);
    return format(yesterday, 'yyyy-MM-dd');
  });
  const [mapLoading, setMapLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [dateRange, filterDate, searchTerm, page, rowsPerPage, weeksBack]);

  // Map-specific useEffect (independent from table)
  useEffect(() => {
    fetchMapEvents();
  }, [mapDate]);

  const fetchMapEvents = async () => {
    try {
      setMapLoading(true);

      // Fetch events for the specific map date
      const response = await api.get('/collection-events/previous', {
        params: {
          days: 365, // Get enough data to go back in time
          search: undefined
        }
      });

      let allEvents = response.data.events || [];

      // Filter to only the selected map date and completed status
      const filteredEvents = allEvents.filter((event: CollectionEvent) => {
        if (!event.scheduled_date || event.status !== 'completed') return false;
        const eventDate = event.scheduled_date.split('T')[0];
        return eventDate === mapDate;
      });

      // Sort by completion time if available
      filteredEvents.sort((a: CollectionEvent, b: CollectionEvent) => {
        if (!a.completed_at || !b.completed_at) return 0;
        return new Date(a.completed_at).getTime() - new Date(b.completed_at).getTime();
      });

      setMapEvents(filteredEvents);
    } catch (error) {
      console.error('Error fetching map events:', error);
      showNotification('Failed to fetch map data', 'error');
    } finally {
      setMapLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      // Calculate days parameter based on weeksBack
      const days = weeksBack * 7;
      
      // Fetch previous completed collection events
      const response = await api.get('/collection-events/previous', {
        params: {
          days: days,
          search: searchTerm || undefined
        }
      });
      
      console.log('Previous Collections API Response:', response.data);
      let allEvents = response.data.events || [];
      console.log('Previous events from API:', allEvents.length, 'events');
      
      // Apply date filter if specified
      if (filterDate) {
        allEvents = allEvents.filter((event: CollectionEvent) => {
          if (!event.scheduled_date) return false;
          // Compare just the date part
          const eventDate = event.scheduled_date.split('T')[0];
          const filterDatePart = filterDate.split('T')[0];
          return eventDate === filterDatePart;
        });
      }
      
      // Apply pagination
      const startIndex = page * rowsPerPage;
      const paginatedEvents = allEvents.slice(startIndex, startIndex + rowsPerPage);
      
      console.log('After all filtering:', allEvents.length, 'events remaining');
      console.log('Paginated events:', paginatedEvents.length);
      
      setEvents(paginatedEvents);
      setTotalCount(allEvents.length);
    } catch (error) {
      console.error('Error fetching previous collection events:', error);
      showNotification('Failed to fetch previous collections', 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleWeeksChange = (weeks: number) => {
    setWeeksBack(weeks);
    setDateRange({
      start: subWeeks(new Date(), weeks),
      end: new Date()
    });
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  const handleViewDetails = async (event: CollectionEvent) => {
    try {
      setLoadingDetails(true);
      // Fetch full order details and all collection events for this order
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${event.order_id}`),
        api.get(`/collection-events/order/${event.order_id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setOrderCollectionEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('Failed to fetch order details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, order: null });
    setOrderCollectionEvents([]);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = events.map(ev => ev.id);
      setSelectedEvents(newSelected);
    } else {
      setSelectedEvents([]);
    }
  };

  const handleSelectOne = (id: number) => {
    const selectedIndex = selectedEvents.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedEvents, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedEvents.slice(1));
    } else if (selectedIndex === selectedEvents.length - 1) {
      newSelected = newSelected.concat(selectedEvents.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedEvents.slice(0, selectedIndex),
        selectedEvents.slice(selectedIndex + 1),
      );
    }

    setSelectedEvents(newSelected);
  };


  const getServiceTypeLabel = (event: CollectionEvent) => {
    const parts = [];
    
    // Calculate total bin count from individual colors
    const totalBins = (event.bin_brown || 0) +
                      (event.bin_green || 0) +
                      (event.bin_blue || 0) +
                      (event.bin_black || 0) +
                      (event.bin_grey || 0) +
                      (event.bin_pink || 0);
    
    // Show the actual counts
    if (totalBins > 0) {
      parts.push(`${totalBins} ${totalBins === 1 ? 'Bin' : 'Bins'}`);
    } else if (event.bins_total_services) {
      // Fallback to bins_total_services if no color breakdown
      parts.push(`${event.bins_total_services} ${event.bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    }
    
    if (event.bags_total_services) {
      parts.push(`${event.bags_total_services} ${event.bags_total_services === 1 ? 'Bag' : 'Bags'}`);
    }
    
    if (event.large_bins_total_services) {
      parts.push(`${event.large_bins_total_services} Large ${event.large_bins_total_services === 1 ? 'Bin' : 'Bins'}`);
    }

    if (event.beds_count) {
      parts.push(`${event.beds_count} ${event.beds_count === 1 ? 'Bed' : 'Beds'}`);
    }

    if (event.mattress_count) {
      parts.push(`${event.mattress_count} ${event.mattress_count === 1 ? 'Mattress' : 'Mattresses'}`);
    }

    return parts.join(', ') || event.service_type || 'Standard';
  };

  const getBinColours = (item: CollectionEvent | Order): string[] => {
    const colours: string[] = [];
    
    // Check individual bin colour counts
    if (item.bin_brown && item.bin_brown > 0) colours.push(`${item.bin_brown} Brown`);
    if (item.bin_green && item.bin_green > 0) colours.push(`${item.bin_green} Green`);
    if (item.bin_blue && item.bin_blue > 0) colours.push(`${item.bin_blue} Blue`);
    if (item.bin_black && item.bin_black > 0) colours.push(`${item.bin_black} Black`);
    if (item.bin_grey && item.bin_grey > 0) colours.push(`${item.bin_grey} Grey`);
    if (item.bin_pink && item.bin_pink > 0) colours.push(`${item.bin_pink} Pink`);
    
    // If no individual counts but bin_colours field exists, try to parse it
    if (colours.length === 0 && item.bin_colours) {
      try {
        const parsedColours = JSON.parse(item.bin_colours);
        if (Array.isArray(parsedColours)) {
          return parsedColours;
        }
      } catch (e) {
        // If not JSON, return as is
        return [item.bin_colours];
      }
    }
    
    return colours;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Previous Collections
      </Typography>

      {/* Map Section - Independent from table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Collections Map
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              type="date"
              label="Map Date"
              size="small"
              value={mapDate}
              onChange={(e) => setMapDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const yesterday = subDays(new Date(), 1);
                setMapDate(format(yesterday, 'yyyy-MM-dd'));
              }}
            >
              Yesterday
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchMapEvents}
            >
              Refresh Map
            </Button>
          </Box>
        </Box>

        <Box sx={{ height: '400px', width: '100%', position: 'relative' }}>
          {mapLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography>Loading map data...</Typography>
            </Box>
          ) : mapEvents.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="text.secondary">
                No completed collections found for {format(new Date(mapDate), 'MMMM d, yyyy')}
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={[53.4808, -2.2426]}
              zoom={11}
              style={{ height: '100%', width: '100%', borderRadius: '4px' }}
              key={mapDate} // Force re-render when date changes
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {mapEvents.map((event, index) => {
                if (event.delivery_latitude && event.delivery_longitude) {
                  return (
                    <Marker
                      key={`map-${event.id}`}
                      position={[
                        parseFloat(event.delivery_latitude.toString()),
                        parseFloat(event.delivery_longitude.toString())
                      ]}
                      icon={completedIcon}
                    >
                      <Popup>
                        <div style={{ minWidth: '250px' }}>
                          <strong>#{index + 1} - Order #{event.order_number}</strong><br />
                          <strong>{event.customer_first_name} {event.customer_last_name}</strong><br />
                          {event.delivery_address_1}<br />
                          {event.delivery_postcode}<br />
                          {event.customer_phone && <><br />📞 {event.customer_phone}</>}
                          <br /><br />
                          <strong>Service:</strong> {getServiceTypeLabel(event)}<br />
                          {getBinColours(event).length > 0 && (
                            <><strong>Bin Colours:</strong> {getBinColours(event).join(', ')}<br /></>
                          )}
                          <strong>Driver:</strong> {event.current_driver_name || event.driver_name || 'Unknown'}<br />
                          {event.completed_at && (
                            <><strong>Completed:</strong> {format(new Date(event.completed_at), 'h:mm a')}<br /></>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                }
                return null;
              })}
            </MapContainer>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {mapEvents.length} completed collection{mapEvents.length !== 1 ? 's' : ''} for {format(new Date(mapDate), 'MMMM d, yyyy')}
          {mapEvents.length > 0 && ' (markers numbered in order of completion time)'}
        </Typography>
      </Paper>

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Order #, Customer, Postcode"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Collection Date"
              variant="outlined"
              size="small"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={weeksBack}
                onChange={(e) => handleWeeksChange(Number(e.target.value))}
                label="Time Range"
              >
                <MenuItem value={1}>Past Week</MenuItem>
                <MenuItem value={2}>Past 2 Weeks</MenuItem>
                <MenuItem value={4}>Past 4 Weeks</MenuItem>
                <MenuItem value={8}>Past 8 Weeks</MenuItem>
                <MenuItem value={12}>Past 3 Months</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
              onClick={() => {
                setFilterDate('');
                setSearchTerm('');
                setWeeksBack(4);
                fetchEvents();
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Collections Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedEvents.length > 0 && selectedEvents.length < events.length}
                  checked={events.length > 0 && selectedEvents.length === events.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Completed Date</TableCell>
              <TableCell>Order #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell sx={{ maxWidth: 250 }}>Address</TableCell>
              <TableCell>Collected</TableCell>
              <TableCell>Bin Colours</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow
                key={event.id}
                hover
                selected={selectedEvents.indexOf(event.id) !== -1}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEvents.indexOf(event.id) !== -1}
                    onChange={() => handleSelectOne(event.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {(() => {
                        try {
                          if (!event.scheduled_date) return 'No date';
                          
                          // Extract just the date part if it's in ISO format
                          const dateString = event.scheduled_date.split('T')[0];
                          
                          // Parse the date string properly for display
                          const [year, month, day] = dateString.split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
                          
                          if (isNaN(date.getTime())) {
                            return dateString;
                          }
                          return format(date, 'EEE, MMM d');
                        } catch (error) {
                          console.error('Date format error:', error, event.scheduled_date);
                          return event.scheduled_date || 'Invalid date';
                        }
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        try {
                          if (!event.scheduled_date) return '';
                          
                          // Extract just the date part if it's in ISO format
                          const dateString = event.scheduled_date.split('T')[0];
                          
                          // Parse the date string properly for display
                          const [year] = dateString.split('-');
                          return year;
                        } catch (error) {
                          return '';
                        }
                      })()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{event.order_number}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {event.customer_first_name} {event.customer_last_name}
                    {(event.loyalty_member ?? 0) > 0 && (event.previous_orders_count ?? 0) > 0 && (
                      <Tooltip title="These are customers with accounts and are part of the loyalty scheme">
                        <Box
                          component="span"
                          sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1, cursor: 'help' }}
                        >
                          *
                        </Box>
                      </Tooltip>
                    )}
                  </Typography>
                  {event.customer_phone && (
                    <Typography variant="caption" color="text.secondary">
                      {event.customer_phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 250 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={event.delivery_address_1}
                  >
                    {event.delivery_address_1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.delivery_postcode}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" fontSize="small" />
                    <Typography variant="body2">
                      {getServiceTypeLabel(event)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="primary">
                    {getBinColours(event).join(', ') || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {event.current_driver_name || event.driver_name || 'Unassigned'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(event)}
                    title="View Details"
                  >
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No previous collections found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderDetailsModal.open}
        order={orderDetailsModal.order}
        collectionEvents={orderCollectionEvents}
        loadingDetails={loadingDetails}
        onClose={handleCloseOrderDetails}
        onOverrideComplete={() => {}} // No-op for previous collections
        onOverrideOrderComplete={() => {}} // No-op for previous collections
        getBinColours={getBinColours}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PreviousCollections;